'use client';

import { c as _c } from "react/compiler-runtime";
import { type Node } from '@react-types/shared';
import classNames from 'classnames';
import { useRouter } from 'next/navigation';
import { useRef } from 'react';
import { type AriaTabListProps, type AriaTabPanelProps, useTab, useTabList, useTabPanel } from 'react-aria';
import Skeleton from 'react-loading-skeleton';
import { Item, type TabListState, useTabListState } from 'react-stately';
import { Button } from '@module/mdrt-org/shared/components/button/button';
import { type MembershipToolkitTabs } from '@module/mdrt-org/shared/utils/types/membership-toolkit-tabs';
import { ButtonVariant } from '@shared/ui-components/button/buttonVariant';
import { GTM_ACTION_CLICK, GTM_EVENT_NAME_FILE_DOWNLOAD, GTM_EVENT_NAME_INTERNAL_LINK_CLICK, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { isDashboardCarouselCardToolkitLinkItem, isGenericToolkitLinkItem, type LinksList, type TabsData } from '../get-membership-toolkit-content';
import { ToolkitItemCard } from './toolkit-item-card/toolkit-item-card';
import styles from './membership-toolkit-tabs-section.module.scss';
export enum LinksListSectionTheme {
  Default = 'default',
  Secondary = 'secondary',
}
const Tab = t0 => {
  const $ = _c(16);
  const {
    item,
    state
  } = t0;
  const {
    key,
    value
  } = item;
  const ref = useRef(null);
  let t1;
  if ($[0] !== key) {
    t1 = {
      key
    };
    $[0] = key;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isSelected,
    tabProps
  } = useTab(t1, state, ref);
  let t2;
  if ($[2] !== value?.Title) {
    t2 = () => {
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
        label: value?.Title,
        link_label: "Membership Kit"
      });
    };
    $[2] = value?.Title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const sendMemberToolkitIntoGTM = t2;
  const t3 = !isSelected;
  let t4;
  if ($[4] !== isSelected || $[5] !== t3) {
    t4 = classNames(styles["membership-toolkit-tabs__tab-item"], styles["membership-toolkit-tabs__tab-item--interactive"], {
      [styles["membership-toolkit-tabs__tab-item--theme-active"]]: isSelected,
      [styles["membership-toolkit-tabs__tab-item--theme-light"]]: t3
    });
    $[4] = isSelected;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = value?.key;
  let t6;
  if ($[7] !== sendMemberToolkitIntoGTM || $[8] !== tabProps) {
    t6 = event => {
      tabProps?.onClick?.(event);
      sendMemberToolkitIntoGTM();
    };
    $[7] = sendMemberToolkitIntoGTM;
    $[8] = tabProps;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = value?.Title;
  let t8;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7 || $[14] !== tabProps) {
    t8 = <button className={t4} data-testid="membership-toolkit-tab-item" key={t5} ref={ref} type="button" {...tabProps} onClick={t6}>{t7}</button>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = tabProps;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
const TabPanel = t0 => {
  const $ = _c(6);
  let props;
  let state;
  if ($[0] !== t0) {
    ({
      state,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = state;
  } else {
    props = $[1];
    state = $[2];
  }
  const ref = useRef(null);
  const {
    tabPanelProps
  } = useTabPanel(props, state, ref);
  const t1 = state.selectedItem?.props.children;
  let t2;
  if ($[3] !== t1 || $[4] !== tabPanelProps) {
    t2 = <div className={styles["membership-toolkit-tabs__content"]} data-testid="membership-toolkit-tab-content" ref={ref} {...tabPanelProps}>{t1}</div>;
    $[3] = t1;
    $[4] = tabPanelProps;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const Tabs = props => {
  const $ = _c(8);
  const state = useTabListState(props);
  const ref = useRef(null);
  const {
    tabListProps
  } = useTabList(props, state, ref);
  let t0;
  if ($[0] !== state || $[1] !== tabListProps) {
    t0 = state.selectedItem ? <div className={styles["membership-toolkit-tabs__list"]} data-testid="membership-toolkit-tab-navigation" ref={ref} {...tabListProps}>{[...state.collection].map(item => <Tab item={item} key={item.key} state={state} />)}</div> : <div className={styles["membership-toolkit-tabs__list"]}>{Array.from({
        length: 6
      }, _temp).map(_temp2)}</div>;
    $[0] = state;
    $[1] = tabListProps;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== state) {
    t1 = state.selectedItem ? <TabPanel key={state.selectedItem?.key} state={state} /> : <div className={styles["membership-toolkit-tabs__content"]}><Skeleton height={32} width={200} /><Skeleton containerClassName={styles["membership-toolkit-tabs__description-skeleton"]} height={90} /><div className={styles["membership-toolkit-links-list"]}><Skeleton containerClassName={styles["membership-toolkit-links-list__item-skeleton"]} height={120} /><Skeleton containerClassName={styles["membership-toolkit-links-list__item-skeleton"]} height={120} /></div></div>;
    $[3] = state;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = <div className={styles["membership-toolkit-tabs"]}>{t0}{t1}</div>;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const LinksListSection = t0 => {
  const $ = _c(28);
  const {
    layout,
    list,
    MembershipToolkitTabsSectionTitle,
    sendMemberToolkitIntoGTM
  } = t0;
  const router = useRouter();
  if (list === null || list.Items.length === 0) {
    return null;
  }
  const t1 = list.Items;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== MembershipToolkitTabsSectionTitle || $[1] !== layout || $[2] !== list.Items || $[3] !== list.Title || $[4] !== router || $[5] !== sendMemberToolkitIntoGTM) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t6;
      if ($[10] !== layout) {
        t6 = link => {
          if (isGenericToolkitLinkItem(link)) {
            if (layout === LinksListSectionTheme.Default) {
              return Boolean(link.DisplayText || link.LinkToContent);
            }
            return Boolean(link.LinkToContent);
          }
          if (isDashboardCarouselCardToolkitLinkItem(link)) {
            return Boolean(link.CardUrl.LinkToContent);
          }
          return false;
        };
        $[10] = layout;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      const filteredItems = t1.filter(t6);
      if (filteredItems.length === 0) {
        t5 = null;
        break bb0;
      }
      if ($[12] !== list.Title) {
        t4 = list.Title && <h4 className={styles["membership-toolkit-links-list__title"]}>{list.Title}</h4>;
        $[12] = list.Title;
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      const t7 = layout === LinksListSectionTheme.Secondary;
      if ($[14] !== t7) {
        t2 = classNames(styles["membership-toolkit-links-list__group"], {
          [styles["membership-toolkit-links-list__group--secondary"]]: t7
        });
        $[14] = t7;
        $[15] = t2;
      } else {
        t2 = $[15];
      }
      let t8;
      if ($[16] !== MembershipToolkitTabsSectionTitle || $[17] !== layout || $[18] !== list.Title || $[19] !== router || $[20] !== sendMemberToolkitIntoGTM) {
        t8 = link_0 => {
          const key = isDashboardCarouselCardToolkitLinkItem(link_0) ? link_0.CardUrl.LinkToContent : link_0.LinkToContent;
          return <li className={styles["membership-toolkit-links-list__group-item"]} key={key}>{layout === LinksListSectionTheme.Secondary || isDashboardCarouselCardToolkitLinkItem(link_0) ? <ToolkitItemCard link={link_0} LinksListSectionTitle={list.Title} MembershipToolkitTabsSectionTitle={MembershipToolkitTabsSectionTitle} sendMemberToolkitIntoGTM={sendMemberToolkitIntoGTM} /> : <Button isSmall={true} onClick={() => {
              const url = link_0.ShouldDownload && link_0.DownloadFileName ? `${link_0.LinkToContent}?download=${link_0.DownloadFileName}` : link_0.LinkToContent ?? "#";
              const parsed = new URL(url, window.location.href);
              if (parsed.origin === window.location.origin) {
                router.push(parsed.pathname + parsed.search + parsed.hash, {
                  scroll: parsed.pathname !== window.location.pathname
                });
              } else {
                window.open(url, link_0.Target);
              }
              sendMemberToolkitIntoGTM({
                LinksListSectionTitle: list.Title,
                linkURL: url,
                MembershipToolkitTabsSectionTitle
              });
            }} target={link_0.Target} variant={ButtonVariant.Tertiary}>{link_0.DisplayText || link_0.LinkToContent}</Button>}</li>;
        };
        $[16] = MembershipToolkitTabsSectionTitle;
        $[17] = layout;
        $[18] = list.Title;
        $[19] = router;
        $[20] = sendMemberToolkitIntoGTM;
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      t3 = filteredItems.map(t8);
    }
    $[0] = MembershipToolkitTabsSectionTitle;
    $[1] = layout;
    $[2] = list.Items;
    $[3] = list.Title;
    $[4] = router;
    $[5] = sendMemberToolkitIntoGTM;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[22] !== t2 || $[23] !== t3) {
    t6 = <ul className={t2}>{t3}</ul>;
    $[22] = t2;
    $[23] = t3;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== t4 || $[26] !== t6) {
    t7 = <div>{t4}{t6}</div>;
    $[25] = t4;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
};
const sendMemberToolkitIntoGTMCallback = (data: MembershipToolkitTabs) => {
  sendGtmEvent({
    action: GTM_ACTION_CLICK,
    carousel_header: data?.LinksListSectionTitle,
    event: GTM_EVENT_USER_INTERACTIONS,
    eventName: GTM_EVENT_NAME_FILE_DOWNLOAD,
    label: data?.MembershipToolkitTabsSectionTitle,
    link_label: 'Membership Kit',
    link_url: data?.linkURL
  });
};
export const MembershipToolkitTabsSection = t0 => {
  const $ = _c(3);
  const {
    tabsAriaLabel,
    tabsData
  } = t0;
  if (!tabsData || tabsData.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== tabsAriaLabel || $[1] !== tabsData) {
    t1 = <Tabs aria-label={tabsAriaLabel} items={tabsData}>{_temp3}</Tabs>;
    $[0] = tabsAriaLabel;
    $[1] = tabsData;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function _temp(_, index) {
  return index + 1;
}
function _temp2(key) {
  return <Skeleton borderRadius={24} containerClassName={styles["membership-toolkit-tabs__item-skeleton"]} height={39} key={key} />;
}
function _temp3(itemData) {
  const {
    Description,
    Layout,
    Sections,
    Title
  } = itemData;
  return <Item><h3>{Title}</h3>{Description && <p className={styles["membership-toolkit-tabs__description"]}>{Description}</p>}<div className={classNames(styles["membership-toolkit-links-list"], {
      [styles["membership-toolkit-links-list--secondary"]]: Layout === "Images"
    })}>{Sections?.map((sectionList, index) => <LinksListSection key={sectionList && sectionList.Title ? sectionList.Title.replaceAll(/\s+/gu, "-") + index : `toolkit-section-${index}`} layout={Layout === "Images" ? LinksListSectionTheme.Secondary : LinksListSectionTheme.Default} list={sectionList} MembershipToolkitTabsSectionTitle={Title} sendMemberToolkitIntoGTM={sendMemberToolkitIntoGTMCallback} />)}</div></Item>;
}